import { useState } from 'react';
import toast from 'react-hot-toast';
import { Calendar, Clock, Crown, History, Pencil, X } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import organizationService from '../../services/organizationService';

/**
 * Plan Validity Card — surfaces plan dates + extension UI for super-admins.
 * Used in OrganizationDetailPage (orgs) and UserDetailPage (solo trainers).
 *
 * Props:
 *   organizationId — ObjectId string. Required for the extend API call.
 *   subscriptionRecord — { plan, status, currentPeriodStart, currentPeriodEnd, extensions[], synthesized? }
 *   onExtended — callback fired after a successful extension; parent re-fetches.
 */

const PRESETS = [
  { label: '+7 days', days: 7 },
  { label: '+30 days', days: 30 },
  { label: '+90 days', days: 90 },
];

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

/**
 * Plan catalogue for the admin change-plan control. Mirrors the list on
 * EditOrganizationPage so both admin surfaces offer the same tiers, and matches
 * the Organization model's plan enum.
 */
const ADMIN_PLANS = [
  { id: 'free',               name: 'Free',               blurb: '3 sessions (one-time), 30 participants, 1 trainer' },
  { id: 'pro',                name: 'Pro',                blurb: '20 sessions/month, 30 participants, 1 trainer' },
  { id: 'pro_plus',           name: 'Pro Plus',           blurb: 'Unlimited sessions, 100 participants, 3 trainers' },
  { id: 'enterprise_starter', name: 'Enterprise Starter', blurb: 'Unlimited sessions, 200 participants, 5 trainers' },
  { id: 'enterprise_scale',   name: 'Enterprise Scale',   blurb: 'Unlimited everything, custom limits' },
];

/**
 * Tiers a super-admin may grant from this control.
 *
 * Complimentary plans for solo trainers are capped at Pro — the same rule the user
 * edit page applies (userController only calls applySoloTrainerPlan for free|pro).
 * Anything above Pro is a real subscription and has to go through Dodo checkout, or
 * the app would hold an entitlement with no payment behind it.
 */
const GRANTABLE_PLAN_IDS = ['free', 'pro'];
const GRANTABLE_PLANS = ADMIN_PLANS.filter((p) => GRANTABLE_PLAN_IDS.includes(p.id));

const PlanValidityCard = ({
  organizationId,
  subscriptionRecord,
  onExtended,
  // 'organization' (default) or 'trainer' — controls copy strings so the
  // same component reads naturally on both /admin/organizations/:id and
  // /admin/users/:id (solo trainer) without losing the shared backend
  // contract or audit log shape.
  context = 'organization',
  // Participant-cap info { plan, planDefault, override, effective }. When
  // provided (solo-trainer page), renders an admin control to raise/reset the
  // per-session participant cap without changing the plan.
  participantLimit,
  onOverrideChanged,
  // Organization.type, supplied by the organization detail page. Solo trainers are
  // reached with context='trainer' instead, where the org is individual by definition.
  orgType,
}) => {
  const subjectNoun = context === 'trainer' ? "trainer's plan" : "organization's plan";
  // Change-plan modal. Separate from the extend modal because they are different
  // operations: extending moves the period end, changing swaps the tier (and the
  // limits that come with it) while leaving the dates alone.
  const [planOpen, setPlanOpen] = useState(false);
  const [planValue, setPlanValue] = useState('');
  const [planSubmitting, setPlanSubmitting] = useState(false);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('preset'); // 'preset' | 'date'
  const [days, setDays] = useState(30);
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  // Participant-cap override state.
  const [pOpen, setPOpen] = useState(false);
  const [pSubmitting, setPSubmitting] = useState(false);
  const [pValue, setPValue] = useState('');

  const fmtCap = (n) => (n === -1 ? 'Unlimited' : n);
  const hasOverride = participantLimit && typeof participantLimit.override === 'number' && participantLimit.override > 0;

  /**
   * Change the plan tier.
   *
   * PUT /organizations/:id updates organization.plan AND subscription.plan, then the
   * pre-save hooks on both recalculate limits and features — so switching to Pro really
   * does grant 20 sessions/month, not just relabel the badge.
   *
   * Deliberately does NOT touch the period dates. Extending and changing are separate
   * operations, and silently adding time to a tier change would make the audit trail
   * lie about what an admin actually did.
   */
  const handlePlanSubmit = async () => {
    if (!planValue || planValue === currentPlan) {
      toast.error('Pick a different plan');
      return;
    }
    try {
      setPlanSubmitting(true);
      const res = await organizationService.updateOrganization(organizationId, { plan: planValue });
      if (res?.success) {
        toast.success(`Plan changed to ${GRANTABLE_PLANS.find((x) => x.id === planValue)?.name || planValue}`);
        setPlanOpen(false);
        setPlanValue('');
        // Reuse the extension callback so the parent refreshes the same way it does
        // after an extend — the card's own data comes from the parent either way.
        if (onExtended) {
          onExtended({
            currentPeriodEnd: sub.currentPeriodEnd,
            currentPeriodStart: sub.currentPeriodStart,
            status: 'active',
            extensions: sub.extensions,
            plan: planValue,
          });
        }
      } else {
        toast.error(res?.error || 'Failed to change plan');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to change plan');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleOverrideSubmit = async (reset = false) => {
    const raw = reset ? null : Number(pValue);
    if (!reset && (!Number.isFinite(raw) || raw < 1)) {
      toast.error('Enter a positive number, or reset to plan default');
      return;
    }
    try {
      setPSubmitting(true);
      const res = await organizationService.setParticipantOverride(organizationId, reset ? null : raw);
      if (res?.success) {
        toast.success(res.message || 'Participant limit updated');
        setPOpen(false);
        setPValue('');
        if (onOverrideChanged) onOverrideChanged(res.data);
      } else {
        toast.error(res?.error || 'Failed to update participant limit');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to update participant limit');
    } finally {
      setPSubmitting(false);
    }
  };

  const sub = subscriptionRecord || {};
  // Tier currently on the subscription record. Used to disable the change-plan submit
  // when the admin picks the plan the org already has, and to mark it in the list.
  const currentPlan = sub.plan || 'free';
  /**
   * Change Plan is for INDIVIDUAL (solo-trainer) orgs only.
   *
   * A standard organisation subscribes through Dodo checkout, and an admin button that
   * silently grants a paid tier would put the app and the payment provider out of sync —
   * the org would hold Pro Plus with no subscription behind it. Extending validity is
   * safe for anyone; changing what they are entitled to is not.
   */
  const canChangePlan = context === 'trainer' || orgType === 'individual';
  // Mirrors the server: a comp plan change restarts a one-month window from today.
  const compPeriodEnd = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d;
  })();

  const isSynthesized = !!sub.synthesized;
  const status = sub.status || 'active';
  const periodStart = sub.currentPeriodStart;
  const periodEnd = sub.currentPeriodEnd;
  const today = new Date();
  const remaining = periodEnd ? Math.max(0, daysBetween(periodEnd, today)) : 0;
  const isExpired = status === 'expired' || (periodEnd && new Date(periodEnd).getTime() < Date.now());
  // Days since the plan ended — only meaningful when expired. Used to render
  // "Plan ended N days ago" instead of a useless "Expired" text in the tile.
  const daysSinceExpiry = isExpired && periodEnd
    ? Math.max(0, daysBetween(today, periodEnd))
    : 0;

  const resetForm = () => {
    setMode('preset');
    setDays(30);
    setEndDate('');
    setReason('');
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (mode === 'preset' && (!days || days <= 0)) {
      toast.error('Pick a positive number of days');
      return;
    }
    if (mode === 'date' && !endDate) {
      toast.error('Pick a new end date');
      return;
    }
    const payload = mode === 'preset'
      ? { addedDays: Number(days), reason: reason.trim() }
      : { newEndDate: endDate, reason: reason.trim() };

    try {
      setSubmitting(true);
      const res = await organizationService.extendPlan(organizationId, payload);
      if (res?.success) {
        toast.success(res.message || 'Plan extended');
        setOpen(false);
        resetForm();
        if (onExtended) onExtended(res.data);
      } else {
        toast.error(res?.error || 'Failed to extend plan');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to extend plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card>
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900">Plan Validity</h3>
              <p className="text-sm text-neutral-500">Manage when this {subjectNoun} ends</p>
            </div>
            <Badge variant={isExpired ? 'error' : status === 'active' ? 'success' : 'default'} className="capitalize">
              {isExpired ? 'expired' : status}
            </Badge>
          </div>

          {isSynthesized && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              No subscription record yet — extending will create one.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="p-3 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                <Calendar size={14} /> Plan Started
              </div>
              <p className="text-sm font-semibold text-neutral-900">{formatDate(periodStart)}</p>
            </div>
            <div className="p-3 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                <Calendar size={14} /> Plan Ends
              </div>
              <p className="text-sm font-semibold text-neutral-900">{formatDate(periodEnd)}</p>
            </div>
            {/* When expired, the tile flips its label and shows how long ago
                the plan ended — more actionable than a flat "Expired" string. */}
            <div className="p-3 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider mb-1">
                <Clock size={14} />
                {isExpired ? 'Plan Ended' : 'Days Remaining'}
              </div>
              <p className={`text-sm font-semibold ${isExpired ? 'text-red-600' : 'text-neutral-900'}`}>
                {isExpired
                  ? (daysSinceExpiry === 0
                      ? 'Today'
                      : `${daysSinceExpiry} ${daysSinceExpiry === 1 ? 'day' : 'days'} ago`)
                  : `${remaining} ${remaining === 1 ? 'day' : 'days'}`}
              </p>
            </div>
          </div>

          <Button variant="primary" onClick={() => setOpen(true)}>
            <Pencil size={14} className="mr-2" />
            {/* When the plan has already lapsed, the same action is also a
                reactivation — the controller flips status:'expired'→'active'
                inside the same call. Clearer affordance for the admin. */}
            {isExpired ? 'Reactivate & Extend' : 'Extend Plan'}
          </Button>

          {canChangePlan && (
            <>
              {/* Change the tier itself.
              Extending and changing solve different problems and were easy to confuse:
              extending an org that has exhausted a Free plan just gives it longer to sit
              at a limit it has already hit. This sits beside Extend so the distinction is
              visible at the point of decision. */}
          <Button variant="secondary" onClick={() => { setPlanValue(currentPlan); setPlanOpen(true); }}>
            <Crown size={14} className="mr-2" />
            Change Plan
          </Button>
            </>
          )}

          {/* Participant-cap override — only when the parent supplies the data
              (solo-trainer page). Lets admin raise the per-session cap without
              changing the plan; it auto-resets if the plan is downgraded. */}
          {participantLimit && (
            <div className="mt-6 pt-5 border-t border-neutral-200">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-secondary-900">Participants per session</h4>
                  <p className="text-2xl font-bold text-neutral-900 mt-1">
                    {fmtCap(participantLimit.effective)}
                    {hasOverride && (
                      <span className="ml-2 align-middle inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200">
                        admin override
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Plan default: {fmtCap(participantLimit.planDefault)} &middot; stays until changed, resets on downgrade
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => { setPValue(hasOverride ? String(participantLimit.override) : ''); setPOpen(true); }}>
                  <Pencil size={14} className="mr-1.5" />
                  {hasOverride ? 'Edit limit' : 'Set custom limit'}
                </Button>
              </div>
            </div>
          )}

          {/* Extension history */}
          {Array.isArray(sub.extensions) && sub.extensions.length > 0 && (
            <div className="mt-6 pt-5 border-t border-neutral-200">
              <div className="flex items-center gap-2 mb-3">
                <History size={16} className="text-neutral-500" />
                <h4 className="text-sm font-semibold text-secondary-900">Extension History</h4>
                <span className="text-xs text-neutral-400">({sub.extensions.length})</span>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {sub.extensions.slice().reverse().map((ext, idx) => {
                  const by = ext.extendedBy;
                  const byName = by
                    ? `${by.firstName || ''} ${by.lastName || ''}`.trim() || by.email || 'Super Admin'
                    : 'Super Admin';
                  return (
                    <div key={idx} className="p-3 bg-white border border-neutral-200 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-neutral-900">
                          +{ext.addedDays} {ext.addedDays === 1 ? 'day' : 'days'}
                        </span>
                        <span className="text-xs text-neutral-400">
                          {formatDate(ext.extendedAt)}
                        </span>
                      </div>
                      <p className="text-xs text-neutral-600 mb-1">
                        {formatDate(ext.previousEndDate)} → <strong>{formatDate(ext.newEndDate)}</strong>
                      </p>
                      <p className="text-xs text-neutral-700">{ext.reason}</p>
                      <p className="text-xs text-neutral-400 mt-1">by {byName}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={open}
        onClose={() => { setOpen(false); resetForm(); }}
        title={isExpired ? 'Reactivate & Extend Plan' : 'Extend Plan'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">How much?</label>
            <div className="flex gap-2 mb-3">
              {PRESETS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => { setMode('preset'); setDays(p.days); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                    mode === 'preset' && days === p.days
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setMode('date')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  mode === 'date'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300'
                }`}
              >
                Custom date
              </button>
            </div>

            {mode === 'preset' ? (
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Days to add</label>
                <input
                  type="number"
                  min="1"
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs text-neutral-500 mb-1">New end date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Customer requested 30-day extension to evaluate Pro plan"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
            />
            <p className="text-xs text-neutral-400 mt-1">Surfaces in audit history. Required.</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => { setOpen(false); resetForm(); }} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>
              {isExpired ? 'Reactivate & Extend' : 'Extend Plan'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Participant-cap override modal */}
      <Modal
        isOpen={pOpen}
        onClose={() => { setPOpen(false); setPValue(''); }}
        title="Set participant limit"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Raise the maximum participants per session for this trainer without changing their plan
            (currently <strong>{participantLimit ? (participantLimit.plan || 'free').replace('_', ' ') : ''}</strong>,
            default {participantLimit ? fmtCap(participantLimit.planDefault) : ''}). It stays until you change it,
            and automatically resets to the plan default if the plan is downgraded.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Max participants per session</label>
            <input
              type="number"
              min="1"
              value={pValue}
              onChange={(e) => setPValue(e.target.value)}
              placeholder={participantLimit ? String(participantLimit.planDefault) : '30'}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>
          <div className="flex justify-between items-center pt-2">
            <Button
              variant="ghost"
              onClick={() => handleOverrideSubmit(true)}
              disabled={pSubmitting || !hasOverride}
            >
              Reset to plan default
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setPOpen(false); setPValue(''); }} disabled={pSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => handleOverrideSubmit(false)} loading={pSubmitting} disabled={pSubmitting}>
                Save limit
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Change plan */}
      <Modal
        isOpen={planOpen}
        onClose={() => { setPlanOpen(false); setPlanValue(''); }}
        title="Change plan"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Switches the tier and its limits immediately, and starts a fresh one-month
            complimentary period from today. Session usage resets with it.
          </p>

          {/* The new window can be SHORTER than the current one — a plan extended far
              into the future drops back to one month. Say so before they confirm. */}
          {sub.currentPeriodEnd && new Date(sub.currentPeriodEnd) > compPeriodEnd && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              This shortens the current period: it ends {formatDate(sub.currentPeriodEnd)}
              {' '}today and would become {formatDate(compPeriodEnd)}. Use
              {' '}<span className="font-medium">Extend Plan</span> afterwards to add more time.
            </p>
          )}

          <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
            New period: <span className="font-medium text-neutral-700">{formatDate(new Date())}</span>
            {' → '}<span className="font-medium text-neutral-700">{formatDate(compPeriodEnd)}</span>
          </div>

          <div className="space-y-2">
            {GRANTABLE_PLANS.map((pl) => {
              const isCurrent = pl.id === currentPlan;
              const selected = planValue === pl.id;
              return (
                <label
                  key={pl.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    selected ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="admin-plan"
                    className="mt-0.5 text-primary-500 focus:ring-primary-500"
                    checked={selected}
                    onChange={() => setPlanValue(pl.id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-neutral-900">
                      {pl.name}
                      {isCurrent && (
                        <span className="ml-2 text-[11px] font-normal text-neutral-500">current</span>
                      )}
                    </span>
                    <span className="block text-xs text-neutral-500 mt-0.5">{pl.blurb}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {/* Free is a signup-only, one-time trial elsewhere in the product; an admin
              setting it deliberately is allowed, but should be a conscious choice. */}
          {planValue === 'free' && planValue !== currentPlan && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              Free is normally a one-time trial granted at signup. Setting it here re-grants
              the 3-session allowance.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => { setPlanOpen(false); setPlanValue(''); }}
              disabled={planSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePlanSubmit}
              disabled={planSubmitting || !planValue || planValue === currentPlan}
            >
              {planSubmitting ? 'Saving…' : 'Change plan'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PlanValidityCard;
